/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { StateService } from '../../services/api/stateService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { apiClient } from '../../services/apiClient';
import { StateHistoryService } from '../../services/api/stateHistoryService';
import { StateMachineReportingService } from '../../services/api/stateMachineReportingService';
import {
  CArtifactStateItem,
  CArtifact,
  CompanyArtifactId,
  PrimeArtifactId,
} from '../../types/artifacts-branded';
import { PrimeArtifactState } from '../../types/primeModel';
import { StateForm } from './StateForm';
import { StateMachineValidator } from './StateMachineValidator';
import styles from './StateManager.module.css';
import { ArtifactStateTransitionType, DemandFlowType, DemandType } from '../../types/enums';
import { ArtifactItemState } from '../../types/artifactItems';
import { canUsePrimeArtifactFeatures } from '../AppShell/appNavAccess';
import { useAppNavAccess } from '../AppShell/useAppNavAccess';

// Union type for states with discriminator
type StateWithType = (CArtifactStateItem | PrimeArtifactState) & {
  isPrimeState: boolean;
};

interface UnifiedStateManagerProps {
  artifactId: CompanyArtifactId;
  usePrime: boolean;
  artifact?: CArtifact; // Company artifact with optional prime reference
}

/**
 * Unified state manager component for managing artifact states and state machine workflows
 * @feature PLATFORM_STATE_MACHINE - Enables state machine management and transitions
 * @feature PLATFORM_ARTIFACT_MODELING - Supports artifact state management
 */
export const UnifiedStateManager: React.FC<UnifiedStateManagerProps> = ({
  artifactId,
  usePrime,
  artifact,
}) => {
  const [states, setStates] = useState<StateWithType[]>([]);
  const [selectedState, setSelectedState] = useState<StateWithType | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navAccess = useAppNavAccess();
  const canApplyFromPrime = canUsePrimeArtifactFeatures(navAccess);

  const loadStates = useCallback(async () => {
    try {
      setLoading(true);
      const allStates: StateWithType[] = [];

      // Always load company states for the artifact
      try {
        // Only log in development mode and sample
        if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
          console.log('🔄 [UnifiedStateManager] Loading company states for artifact (sampled):', artifactId);
        }
        const companyStates = await StateService.getStatesByArtifact(artifactId);
        
        if (companyStates && companyStates.length > 0) {
          allStates.push(
            ...companyStates.map(
              (state) => ({ ...state, isPrimeState: false }) as unknown as StateWithType
            )
          );
        }
      } catch (err: any) {
        console.error('❌ [UnifiedStateManager] Failed to load company states:', err);
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ [UnifiedStateManager] Error details:', {
            message: err?.message,
            response: err?.response?.data,
            status: err?.response?.status,
          });
        }
        setError(`Failed to load company states: ${err?.message || 'Unknown error'}`);
      }

      // Also load prime states if the artifact has a prime connection
      const primeArtifactId = artifact?.prime_artifact_id;
      if (primeArtifactId) {
        try {
          // Only log in development mode and sample
          if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
            console.log('🔄 [UnifiedStateManager] Loading prime states for prime artifact (sampled):', primeArtifactId);
          }
          const primeStates = await PrimeArtifactService.getPrimeArtifactStates(primeArtifactId);
          
          if (primeStates && primeStates.length > 0) {
            allStates.push(
              ...primeStates.map(
                (state) => ({ ...state, isPrimeState: true }) as unknown as StateWithType
              )
            );
          }
        } catch (err: any) {
          console.error('❌ [UnifiedStateManager] Failed to load prime states:', err);
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ [UnifiedStateManager] Prime states error details:', {
              message: err?.message,
              response: err?.response?.data,
              status: err?.response?.status,
            });
          }
        }
      }

      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('✅ [UnifiedStateManager] Total states loaded (sampled):', allStates.length);
      }
      setStates(allStates);
    } catch (err) {
      setError('Failed to load states');
      console.error('Error loading states:', err);
    } finally {
      setLoading(false);
    }
  }, [artifactId, artifact]);

  useEffect(() => {
    void loadStates();
  }, [artifactId, loadStates]);

  const handleStateSelect = (state: StateWithType) => {
    setSelectedState(state);
    setIsEditing(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCreateClick = () => {
    setSelectedState(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedState(null);
    setError(null);
  };

  const handleDelete = async (stateId: string) => {
    try {
      const stateToDelete = states.find((state) => state.id === stateId);
      if (!stateToDelete) {
        setError('State not found');
        return;
      }

      if (stateToDelete.isPrimeState) {
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        await PrimeArtifactService.deletePrimeArtifactState(primeArtifactId, stateId);
      } else {
        await StateService.deleteState(stateId, artifactId);
      }
      setStates(states.filter((state) => state.id !== stateId));
      if (selectedState?.id === stateId) {
        setSelectedState(null);
      }
    } catch (err) {
      setError('Failed to delete state');
      console.error('Error deleting state:', err);
    }
  };

  const handleStateUpdate = async (state: Partial<CArtifactStateItem>) => {
    if (!state.id) {
      setError('Cannot update state without ID');
      return;
    }
    try {
      const existingState = states.find((s) => s.id === state.id);
      if (!existingState) {
        setError('State not found');
        return;
      }

      let updatedState: CArtifactStateItem | PrimeArtifactState | ArtifactItemState;
      if (existingState.isPrimeState) {
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        updatedState = await PrimeArtifactService.updatePrimeArtifactState(state.id, state);
      } else {
        updatedState = await StateService.updateState(artifactId, state.id, state);
      }

      // Preserve the isPrimeState flag and handle type differences
      const updatedStateWithFlag = {
        ...updatedState,
        isPrimeState: existingState.isPrimeState,
      } as StateWithType;
      setStates(states.map((s) => (s.id === state.id ? updatedStateWithFlag : s)));
      setSelectedState(updatedStateWithFlag);
      setIsEditing(true);
      setError(null);
    } catch (err: any) {
      let errorMessage = 'Failed to update state';
      
      // Provide more helpful error messages
      if (err?.code === 'ERR_NETWORK' || err?.message?.includes('CORS') || err?.message?.includes('Network Error')) {
        errorMessage = 'Network error: The backend server may not be configured to accept requests from this origin. Please check CORS configuration on the backend.';
      } else if (err?.response?.status === 500) {
        errorMessage = 'Server error: The backend encountered an error processing the update request. Please check the backend logs for details.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error updating state:', err);
    }
  };

  const handleStateCreate = async (state: Partial<CArtifactStateItem>) => {
    try {
      let newState: StateWithType;
      if (usePrime) {
        // Use prime_artifact_id when usePrime is true
        const primeArtifactId = artifact?.prime_artifact_id;
        if (!primeArtifactId) {
          setError('No prime artifact associated with this artifact');
          return;
        }
        // Convert enum values to lowercase and use prime_artifact_id for backend
        const stateData: any = {
          name: state.name || '',
          description: state.description || '',
          prime_artifact_id: primeArtifactId, // Backend expects prime_artifact_id, not prime_artifact_ref
          is_improvement: false,
          state_type: ArtifactStateTransitionType.OPERATIONAL,
          demand_type: DemandType.TARGET.toLowerCase(), // Backend expects lowercase: 'target', 'supply', 'capacity'
          demand_flow_type: DemandFlowType.DRUM.toLowerCase(), // Backend expects lowercase: 'push', 'pull', 'drum'
          is_required: false,
          state_metadata: {},
          state_rules: {},
          state_parameters: {},
          validation_rules: {},
          is_initial: false,
          is_final: false,
          is_resource_active: false,
        };
        
        const createdPrimeState = await PrimeArtifactService.createPrimeArtifactState(stateData);
        newState = { ...createdPrimeState, isPrimeState: true } as StateWithType;
      } else {
        const createdState = await StateService.createState(artifactId, state);
        newState = { ...createdState, isPrimeState: false } as unknown as StateWithType;
      }
      setStates([...states, newState]);
      setSelectedState(newState);
      setIsEditing(true);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      const errorMessage = err?.message || err?.response?.data?.detail || 'Failed to create state';
      setError(errorMessage);
      console.error('Error creating state:', err);
      console.error('Error details:', err?.response?.data);
    }
  };

  const handleCreateCompanyStateFromPrime = async (primeState: StateWithType) => {
    try {
      if (!primeState.isPrimeState) {
        setError('Can only create company states from prime states');
        return;
      }

      // Extract state_type from prime state (PrimeArtifactState has state_type)
      const primeStateTyped = primeState as PrimeArtifactState;
      const stateType = primeStateTyped.state_type;

      // Create a company state based on the prime state
      // Use Partial<ArtifactItemState> with additional fields allowed via Record<string, any>
      // to include prime_state_ref which is accepted by the backend but not in ArtifactItemState type
      const companyStateData: Partial<ArtifactItemState> & Record<string, any> = {
        name: primeState.name,
        description: primeState.description || '',
        state_type: stateType, // REQUIRED: state_type must be included
        is_initial: primeState.is_initial || false,
        is_final: primeState.is_final || false,
        prime_state_ref: primeState.id, // Link to the prime state (accepted by backend)
      };

      const createdState = await StateService.createState(artifactId, companyStateData);
      const newState = { ...createdState, isPrimeState: false } as unknown as StateWithType;

      setStates([...states, newState]);
      setSelectedState(newState);
      setError(null);
    } catch (err: any) {
      let errorMessage = 'Failed to create company state from prime state';
      
      // Provide more helpful error messages
      if (err?.code === 'ERR_NETWORK' || err?.message?.includes('CORS') || err?.message?.includes('Network Error')) {
        errorMessage = 'Network error: The backend server may not be configured to accept requests from this origin. Please check CORS configuration on the backend.';
      } else if (err?.response?.status === 500) {
        errorMessage = 'Server error: The backend encountered an error processing the request. Please check the backend logs for details.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Error creating company state from prime state:', err);
    }
  };

  const handleApplyFromPrime = async () => {
    if (!artifact?.prime_artifact_id) {
      setError('This artifact is not linked to a prime artifact. Cannot apply from prime.');
      return;
    }

    const confirmed = window.confirm(
      'This will DELETE all existing states for this company artifact and replace them with states from the linked prime artifact.\n\nThis action cannot be undone.\n\nAre you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.post(
        `/artifacts/${artifactId}/apply-prime-states`,
        null,
        { params: { prime_artifact_id: artifact.prime_artifact_id } }
      );
      
      // Refresh states after application
      await loadStates();
      
      alert(`Prime states applied successfully! Created ${response.data.states_created || 0} states.`);
    } catch (err: any) {
      // Extract detailed error message
      let errorMessage = 'Failed to apply prime states';
      if (err?.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail);
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      // Provide more helpful error messages for common cases
      if (err?.response?.status === 500) {
        errorMessage = `Server error: ${errorMessage}. Please check the backend logs for details.`;
      } else if (err?.response?.status === 404) {
        errorMessage = `Artifact or prime artifact not found. ${errorMessage}`;
      } else if (err?.response?.status === 400) {
        errorMessage = `Invalid request: ${errorMessage}`;
      }
      
      setError(errorMessage);
      console.error('Error applying prime states:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group states by name for compact display
  const groupedStates = states.reduce(
    (groups, state) => {
      const name = state.name;
      if (!groups[name]) {
        groups[name] = { prime: null, company: null };
      }
      if (state.isPrimeState) {
        groups[name].prime = state;
      } else {
        groups[name].company = state;
      }
      return groups;
    },
    {} as Record<string, { prime: StateWithType | null; company: StateWithType | null }>
  );

  if (loading) {
    return <div>Loading states...</div>;
  }

  return (
    <div className={styles.stateManager}>
      {!usePrime && artifactId && (
        <div style={{ marginBottom: 16 }}>
          <StateMachineValidator artifactId={artifactId} autoValidate={false} compact />
        </div>
      )}
      <div className={styles.stateManagerHeader}>
        <h2>States ({usePrime ? 'Creating Prime' : 'Creating Regular'})</h2>
        <div className={styles.stateManagerActions}>
          {artifact?.prime_artifact_id && (
            <button
              onClick={handleApplyFromPrime}
              className={styles.applyFromPrimeButton}
              disabled={loading || !canApplyFromPrime}
              title={
                canApplyFromPrime
                  ? 'Apply all states from linked prime artifact'
                  : 'Requires prime_artifacts subscription or platform admin access'
              }
            >
              Apply from Prime
            </button>
          )}
          <button onClick={handleCreateClick} className={styles.addStateButton}>
            Add State
          </button>
        </div>
      </div>

      <div className={styles.stateManagerContent}>
        <div className={styles.stateList}>
          {Object.entries(groupedStates).map(([name, group]) => {
            const primeState = group.prime;
            const companyState = group.company;
            const hasPrime = !!primeState;
            const hasCompany = !!companyState;

            return (
              <div
                key={name}
                className={`${styles.stateGroup} ${selectedState?.name === name ? styles.selected : ''}`}
                onClick={() => {
                  const stateToSelect = companyState || primeState;
                  if (stateToSelect) {
                    handleStateSelect(stateToSelect);
                  }
                }}
              >
                <div className={styles.stateGroupHeader}>
                  <div className={styles.stateNameSection}>
                    <span className={styles.stateName}>{name}</span>
                    <div className={styles.stateActionsInline}>
                      {hasPrime && !hasCompany && (
                        <button
                          className={`${styles.iconButton} ${styles.createCompany}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCreateCompanyStateFromPrime(primeState!);
                          }}
                          title="Create Company State from Prime State"
                        >
                          +
                        </button>
                      )}
                      {hasCompany && (
                        <>
                          <button
                            className={`${styles.iconButton} ${styles.edit}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStateSelect(companyState!);
                            }}
                            title="Edit Company State"
                          >
                            ✏️
                          </button>
                          <button
                            className={`${styles.iconButton} ${styles.delete}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDelete(companyState!.id);
                            }}
                            title="Delete Company State"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.stateBadges}>
                    {hasPrime && <span className={styles.primeBadge}>Prime</span>}
                    {hasCompany && <span className={styles.companyBadge}>Company</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.stateDetails}>
          {selectedState || isEditing ? (
            <StateForm
              inline={true}
              onSubmit={selectedState ? handleStateUpdate : handleStateCreate}
              onCancel={handleCancel}
              initialState={
                selectedState ? (selectedState as unknown as CArtifactStateItem) : undefined
              }
              usePrime={usePrime}
              artifactId={usePrime ? artifact?.prime_artifact_id || artifactId : artifactId}
            />
          ) : (
            <div className={styles.noSelection}>Select a state or create a new one</div>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
};
